import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  TrendingUp as TrendingUpIcon,
  Storage as StorageIcon,
  Assessment as AssessmentIcon,
  Print as PrintIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  AutoGraph as AutoGraphIcon,
  DataObject as DataObjectIcon,
  Cloud as CloudIcon,
  Architecture as ArchitectureIcon,
  Analytics as AnalyticsIcon,
  Gavel as GavelIcon,
  DeviceHub as DeviceHubIcon,
  Memory as MemoryIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';

const NPDRoadmap: React.FC = () => {
  // Current state features
  const currentFeatures = [
    {
      category: 'Data Integration',
      icon: <DeviceHubIcon />,
      features: [
        'Integration with 15+ USCIS core systems',
        'Real-time data synchronization',
        'Automated ETL pipelines',
        'Cross-system data harmonization',
        'Legacy system connectivity',
      ],
      status: 'Implemented',
      color: '#4caf50',
    },
    {
      category: 'System Architecture',
      icon: <ArchitectureIcon />,
      features: [
        'Cloud-based infrastructure',
        'Scalable data warehouse',
        'High-availability design',
        'Disaster recovery capabilities',
        'Performance optimization',
      ],
      status: 'Implemented',
      color: '#4caf50',
    },
    {
      category: 'Data Governance',
      icon: <GavelIcon />,
      features: [
        'Data quality monitoring',
        'Access control framework',
        'Audit trail logging',
        'Compliance reporting',
        'Data stewardship model',
      ],
      status: 'Implemented',
      color: '#4caf50',
    },
    {
      category: 'Analytics & Reporting',
      icon: <AnalyticsIcon />,
      features: [
        'Executive dashboards',
        'Case processing metrics',
        'Trend analysis tools',
        'Custom report builder',
        'Data visualization',
      ],
      status: 'Partial',
      color: '#ff9800',
    },
  ];

  // Enhancement opportunities
  const enhancements = [
    {
      category: 'AI/ML Integration',
      priority: 'High',
      features: [
        'Predictive case processing time estimation',
        'Fraud detection using machine learning',
        'Automated case classification',
        'Natural language processing for documents',
        'Anomaly detection in application patterns',
      ],
      effort: 'Large',
      value: 'Very High',
    },
    {
      category: 'Advanced Analytics',
      priority: 'High',
      features: [
        'Real-time operational dashboards',
        'Predictive workload forecasting',
        'Resource optimization analytics',
        'Backlog trend analysis',
        'Performance benchmarking',
      ],
      effort: 'Medium',
      value: 'Very High',
    },
    {
      category: 'Data Quality Enhancement',
      priority: 'High',
      features: [
        'Automated data quality scoring',
        'Real-time validation rules',
        'Data profiling and monitoring',
        'Duplicate detection and resolution',
        'Data standardization automation',
      ],
      effort: 'Medium',
      value: 'High',
    },
    {
      category: 'API & Integration Expansion',
      priority: 'Medium',
      features: [
        'RESTful API for external systems',
        'Real-time event streaming',
        'Microservices architecture',
        'Third-party system connectors',
        'API gateway implementation',
      ],
      effort: 'Large',
      value: 'High',
    },
    {
      category: 'Self-Service Analytics',
      priority: 'Medium',
      features: [
        'User-friendly query builder',
        'Drag-and-drop report designer',
        'Scheduled report distribution',
        'Mobile analytics access',
        'Collaborative workspaces',
      ],
      effort: 'Medium',
      value: 'High',
    },
    {
      category: 'Data Lineage & Impact Analysis',
      priority: 'Medium',
      features: [
        'End-to-end data lineage tracking',
        'Impact analysis for system changes',
        'Data flow visualization',
        'Dependency mapping',
        'Change impact reports',
      ],
      effort: 'Large',
      value: 'Medium',
    },
    {
      category: 'Advanced Security',
      priority: 'High',
      features: [
        'Enhanced encryption at rest and in transit',
        'Multi-factor authentication',
        'Role-based access control (RBAC) enhancement',
        'Data masking for PII/PHI',
        'Security threat monitoring',
      ],
      effort: 'Medium',
      value: 'Very High',
    },
  ];

  // Implementation roadmap
  const roadmapPhases = [
    {
      phase: 'Phase 1: AI/ML Foundation',
      timeline: '1-3 Months',
      focus: 'Intelligence & Automation',
      items: [
        'Implement predictive case processing models',
        'Deploy fraud detection algorithms',
        'Automated case classification system',
        'ML model training infrastructure',
        'Performance monitoring dashboard',
      ],
    },
    {
      phase: 'Phase 2: Analytics Enhancement',
      timeline: '3-5 Months',
      focus: 'Insights & Visualization',
      items: [
        'Real-time operational dashboards',
        'Workload forecasting tools',
        'Resource optimization analytics',
        'Self-service query builder',
        'Mobile analytics platform',
      ],
    },
    {
      phase: 'Phase 3: Data Quality & Security',
      timeline: '5-7 Months',
      focus: 'Quality & Compliance',
      items: [
        'Automated data quality scoring',
        'Enhanced encryption implementation',
        'Multi-factor authentication rollout',
        'PII/PHI data masking',
        'Real-time validation framework',
      ],
    },
    {
      phase: 'Phase 4: Integration & Lineage',
      timeline: '7-9 Months',
      focus: 'Connectivity & Transparency',
      items: [
        'RESTful API development',
        'Event streaming architecture',
        'Data lineage visualization',
        'Impact analysis tools',
        'Third-party connectors',
      ],
    },
  ];

  return (
    <>
      {/* Print Styles */}
      <style>
        {`
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            @page {
              size: letter;
              margin: 0.5in;
            }
            body {
              margin: 0 !important;
              padding: 0 !important;
            }
            /* Hide navigation elements */
            nav, .MuiDrawer-root, .MuiAppBar-root {
              display: none !important;
            }
            /* Preserve Grid layout for columns */
            .MuiGrid-container {
              display: flex !important;
              flex-wrap: wrap !important;
            }
            .MuiGrid-item {
              flex-shrink: 0 !important;
            }
            /* 4-column layout for summary cards */
            .MuiGrid-grid-xs-12.MuiGrid-grid-md-3 {
              flex: 0 0 25% !important;
              max-width: 25% !important;
            }
            /* 2-column layout for capability cards and roadmap phases */
            .MuiGrid-grid-xs-12.MuiGrid-grid-md-6 {
              flex: 0 0 50% !important;
              max-width: 50% !important;
            }
            /* 3-column layout for benefits cards */
            .MuiGrid-grid-xs-12.MuiGrid-grid-md-4 {
              flex: 0 0 33.333% !important;
              max-width: 33.333% !important;
            }
            /* Ensure colors are preserved */
            .MuiPaper-root, .MuiCard-root, .MuiChip-root, .MuiAlert-root, .MuiButton-root {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            /* Preserve table styling */
            .MuiTable-root {
              page-break-inside: auto !important;
            }
            .MuiTableRow-root {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            /* Prevent page breaks inside cards */
            .MuiCard-root, .MuiPaper-root {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            /* Hide print button */
            .print-button {
              display: none !important;
            }
            /* Remove top spacing from main container */
            .MuiBox-root {
              margin-top: 0 !important;
            }
          }
        `}
      </style>

      <Box sx={{ width: '100%', maxWidth: '1400px', mx: 'auto', mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 700 }}>
              National Production Dataset (NPD) Roadmap
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Current State Analysis & Future Enhancement Strategy
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            className="print-button"
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' },
            }}
          >
            Print to PDF
          </Button>
        </Box>

        {/* Executive Summary */}
        <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976d2' }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
            Executive Summary
          </Typography>
          <Typography variant="body1" paragraph>
            The National Production Dataset (NPD) serves as USCIS's central repository for integrated immigration case data. This document outlines our current capabilities, identifies strategic enhancement opportunities, and presents a 9-month roadmap for advancing NPD into an AI-powered, analytics-driven platform.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: '#4caf50', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>15+</Typography>
                  <Typography variant="body2">Integrated Systems</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: '#2196f3', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>7</Typography>
                  <Typography variant="body2">Enhancement Areas</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: '#ff9800', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>4</Typography>
                  <Typography variant="body2">Implementation Phases</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: '#9c27b0', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>9</Typography>
                  <Typography variant="body2">Months Timeline</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Current State Assessment */}
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mt: 5, mb: 3 }}>
          Current State: What We Have Built
        </Typography>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {currentFeatures.map((capability, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ height: '100%', borderLeft: `4px solid ${capability.color}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ color: capability.color, mr: 2 }}>{capability.icon}</Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {capability.category}
                      </Typography>
                      <Chip
                        label={capability.status}
                        size="small"
                        sx={{
                          bgcolor: capability.color,
                          color: 'white',
                          fontWeight: 600,
                          mt: 0.5,
                        }}
                      />
                    </Box>
                  </Box>
                  <List dense>
                    {capability.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon fontSize="small" sx={{ color: capability.color }} />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Future Enhancements */}
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mt: 5, mb: 3 }}>
          Future Enhancements: Strategic Opportunities
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Strategic Vision</AlertTitle>
          Transform NPD into an AI-powered, self-service analytics platform with enhanced security, real-time insights, and comprehensive data lineage capabilities.
        </Alert>

        <TableContainer component={Paper} sx={{ mb: 5 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Enhancement Area</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Priority</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Key Features</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Effort</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Business Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {enhancements.map((enhancement, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' } }}>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {enhancement.category}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={enhancement.priority}
                      size="small"
                      color={
                        enhancement.priority === 'High'
                          ? 'error'
                          : enhancement.priority === 'Medium'
                          ? 'warning'
                          : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <List dense sx={{ py: 0 }}>
                      {enhancement.features.slice(0, 3).map((feature, idx) => (
                        <ListItem key={idx} sx={{ py: 0, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <RadioButtonUncheckedIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                      {enhancement.features.length > 3 && (
                        <Typography variant="caption" color="text.secondary" sx={{ pl: 3 }}>
                          +{enhancement.features.length - 3} more features
                        </Typography>
                      )}
                    </List>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={enhancement.effort}
                      size="small"
                      variant="outlined"
                      color={
                        enhancement.effort === 'Large'
                          ? 'error'
                          : enhancement.effort === 'Medium'
                          ? 'warning'
                          : 'success'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={enhancement.value}
                      size="small"
                      sx={{
                        bgcolor:
                          enhancement.value === 'Very High'
                            ? '#4caf50'
                            : enhancement.value === 'High'
                            ? '#2196f3'
                            : '#ff9800',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Implementation Roadmap */}
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mt: 5, mb: 3 }}>
          Implementation Roadmap: 9-Month Plan
        </Typography>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {roadmapPhases.map((phase, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: '100%',
                  borderLeft: `6px solid ${
                    index === 0
                      ? '#4caf50'
                      : index === 1
                      ? '#2196f3'
                      : index === 2
                      ? '#ff9800'
                      : '#9c27b0'
                  }`,
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {phase.phase}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Focus: {phase.focus}
                      </Typography>
                    </Box>
                    <Chip
                      label={phase.timeline}
                      sx={{
                        bgcolor:
                          index === 0
                            ? '#4caf50'
                            : index === 1
                            ? '#2196f3'
                            : index === 2
                            ? '#ff9800'
                            : '#9c27b0',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <List dense>
                    {phase.items.map((item, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <TrendingUpIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={item}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Key Benefits */}
        <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#f1f8e9', border: '1px solid #aed581' }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#558b2f', fontWeight: 600 }}>
            Expected Benefits & ROI
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SpeedIcon sx={{ fontSize: 40, color: '#558b2f', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#558b2f' }}>
                      Operational Efficiency
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="30% faster case processing insights" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="50% reduction in manual reporting" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Real-time decision support" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SecurityIcon sx={{ fontSize: 40, color: '#558b2f', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#558b2f' }}>
                      Security & Compliance
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Enhanced PII/PHI protection" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Automated compliance monitoring" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Comprehensive audit trails" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AutoGraphIcon sx={{ fontSize: 40, color: '#558b2f', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#558b2f' }}>
                      Strategic Intelligence
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="AI-powered fraud detection" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Predictive workload forecasting" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Data-driven policy insights" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Next Steps */}
        <Paper elevation={2} sx={{ p: 4, bgcolor: '#fff3e0', border: '2px solid #ff9800' }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#ff9800', fontWeight: 600 }}>
            Recommended Next Steps
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="1. Executive Stakeholder Review"
                secondary="Present roadmap to USCIS leadership and secure approval for Phase 1 AI/ML initiatives"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="2. Resource Planning & Budget Allocation"
                secondary="Identify technical resources, data scientists, and budget for 9-month implementation"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="3. AI/ML Proof of Concept"
                secondary="Start with fraud detection and case processing prediction models for quick wins"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="4. Security Assessment"
                secondary="Conduct comprehensive security review and plan enhanced encryption implementation"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="5. Success Metrics Definition"
                secondary="Establish KPIs for measuring enhancement impact and ROI across all phases"
              />
            </ListItem>
          </List>
        </Paper>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            USCIS Data Literacy Support Platform | National Production Dataset Roadmap
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Document Date: October 8, 2025 | Version 1.0
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default NPDRoadmap;
